import { useEffect, useState } from 'react';
import { useInit } from '../hooks/useInit';
import { useOrchestrator } from '../hooks/useOrchestrator';
import { useChat } from '../hooks/useChat';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useLiveVoice } from '../hooks/useLiveVoice';
import { PersonaHeader } from './PersonaHeader';
import { DotWidget } from './DotWidget';
import { LargeDotWidget, type LargeDotMode } from './LargeDotWidget';
import {
  WarpWidget,
  WARP_CHANNELS,
  type WarpMode,
  type WarpThemeChannel,
} from './Warp';
// Stage 200: GPP Laughter Module — first GPP pillar. Render-null
// subscriber that posts Shape-Q engrams on finally-ACCEPTed Gadfly bites
// at turn-end. Mounted as a sibling to WarpWidget; gated by
// laughter_enabled. See stage200-Laughter-Modules-adapted-integration-plan.md §10.7.
import { LaughterSubscriber, WitnessReframeSubscriber } from '../modules/Laughter';
import { ChatTranscript } from './ChatTranscript';
import { InputBar } from './InputBar';
import { SettingsModal } from './SettingsModal';
import { SkillsModal } from './SkillsModal';
import { YesterdayTab } from './YesterdayTab';
import { LiveVoiceButton } from './LiveVoiceButton';
import { LiveTranscriptPanel } from './LiveTranscriptPanel';
import { ProjectsView } from './ProjectsView';
import { GalleryView } from './GalleryView';
import { ImageGallery } from './ImageGallery';
import { MusicGallery } from './MusicGallery';
import { SpeechGallery } from './SpeechGallery';
import { VideoGallery } from './VideoGallery';
import { DocumentGallery } from './DocumentGallery';
// Stage 33: Bulletin Canvas — endless panel canvas at #/bulletin.
import { BulletinRoute } from '../bulletin/BulletinRoute';
// Stage 44: LIBRARIAN-Wiki — knowledge wiki at #/wiki.
import { WikiRoute } from '../wiki/WikiRoute';
// Stage 48: Remote Gallery Connections — operator-only page for minting
// per-app API tokens.
import { ConnectionsView } from './ConnectionsView';
import { ConversationSidebar } from './ConversationSidebar';
import { listPins } from '../zerovoice/pinsClient';
import { kokoroClient } from '../zerovoice/kokoroClient';
import { voiceBundle } from '../zerovoice/voiceBundle';
import { useZeroTTS } from '../zerovoice/useZeroTTS';
import { asZeroTTSDepth } from '../zerovoice/zeroTTSProfile';
import { listVoicePresets, type VoicePresetLite } from '../api/voicePresets';
import type { HudState } from './PersonaHeader';
import type {
  KokoroBlendMode,
  KokoroGender,
  KokoroPin,
  TTSProvider,
  VoiceSpec,
} from '../zerovoice/types';

import { useHashRoute, type PrimaryView } from '../hooks/useHashRoute';

function parseLargeMode(
  raw: string | undefined,
  fallback: LargeDotMode,
): LargeDotMode {
  return raw === 'liquid' || raw === 'flow' || raw === 'static'
    ? raw
    : fallback;
}

function SpeakerIcon({ muted }: { muted: boolean }): JSX.Element {
  // Minimal speaker + cross overlay when muted. No external icon lib.
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M4 9v6h4l5 4V5L8 9H4z"
      />
      {muted ? (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          d="M16 8l6 8M22 8l-6 8"
        />
      ) : (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7 7 0 0 1 0 11"
        />
      )}
    </svg>
  );
}

export function AuthedApp({
  userEmail,
  signOut,
}: {
  userEmail: string;
  signOut: () => Promise<void>;
}) {
  const { init, loading, error, refresh } = useInit();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [view, setView] = useHashRoute();
  const orchestrator = useOrchestrator();
  const audio = useAudioPlayer();

  const speakSetting = init?.settings?.speak_responses;
  const speakResponses = speakSetting === undefined ? true : speakSetting === 'true';
  const liveVoiceEnabled = init?.settings?.permission_live_voice === 'true';
  const videoGenEnabled = init?.settings?.permission_video_gen === 'true';
  // Stage 41: independent flag for veo-video-tool. The Video tab/gallery
  // is treated as "generation enabled" when EITHER video tool is on.
  const veoVideoGenEnabled =
    init?.settings?.permission_veo_video_gen === 'true';
  const anyVideoGenEnabled = videoGenEnabled || veoVideoGenEnabled;
  // Voice is resolved server-side from the `default_tts_voice` setting
  // (the Settings → Voice dropdown). Per-persona voice selection is future work.

  // --- Stage 7: ZeroVoice (Kokoro) settings ---
  const ttsProvider: TTSProvider =
    init?.settings?.tts_provider === 'kokoro' ? 'kokoro' : 'gemini';
  const kokoroWorldSeed =
    (init?.settings?.kokoro_world_seed || 'DEADBEEFC0FFEE').toUpperCase();
  const rawDefaultGender = init?.settings?.kokoro_gender_default;
  const kokoroDefaultGender: KokoroGender =
    rawDefaultGender === 'female' ||
    rawDefaultGender === 'male' ||
    rawDefaultGender === 'mixed'
      ? rawDefaultGender
      : 'mixed';
  const kokoroFallbackToGemini =
    init?.settings?.kokoro_fallback_to_gemini !== 'false';
  const kokoroBlendMode: KokoroBlendMode =
    init?.settings?.kokoro_blend_mode === 'blend' ? 'blend' : 'single';
  const activeAgentName = init?.activePersona?.name ?? '';

  // Stage 9: speech-summary settings.
  const speechSummaryEnabled =
    init?.settings?.speech_summary_enabled !== 'false';
  const speechFallbackRaw = init?.settings?.speech_summary_fallback;
  const speechFallback: 'first-sentence' | 'full' | 'skip' =
    speechFallbackRaw === 'full' || speechFallbackRaw === 'skip'
      ? speechFallbackRaw
      : 'first-sentence';

  // Stage 43: DOT speech-visualization widget settings.
  const dotEnabled = init?.settings?.dot_enabled === 'true';
  const dotActiveProfileId =
    init?.settings?.dot_active_profile_id || 'dot-default-v4';
  const dotColor = init?.settings?.dot_color || '#7dd3fc';
  const dotWireframe = init?.settings?.dot_wireframe === 'true';
  const dotSensitivity = (() => {
    const raw = parseFloat(init?.settings?.dot_sensitivity ?? '0.3');
    return Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0.3;
  })();

  // Stage 45: Large DOT sidebar widget settings. The widget only
  // mounts when enabled === true AND the current view is 'chat'.
  const dotLargeEnabled = init?.settings?.dot_large_enabled === 'true';
  const dotLargeMoodBg = init?.settings?.dot_large_mood_bg !== 'false';
  const dotLargeIdleMode = parseLargeMode(
    init?.settings?.dot_large_idle_mode,
    'liquid',
  );
  const dotLargeWorkingMode = parseLargeMode(
    init?.settings?.dot_large_working_mode,
    'flow',
  );

  // Stage 97: WARP / ZERO Confusion Monitor settings. Frontend-only
  // flags — the worker never reads them. `warp_mode='substitute'`
  // forces LargeDotWidget's `enabled` prop to false regardless of
  // `dot_large_enabled` (the audio-bus cleanup inside LargeDotWidget
  // depends on the existing `if (!enabled) return null` branch, so we
  // route the substitution through the prop, not a JSX unmount).
  const warpEnabled = init?.settings?.warp_enabled === 'true';
  const warpMode: WarpMode =
    init?.settings?.warp_mode === 'substitute'
      ? 'substitute'
      : init?.settings?.warp_mode === 'off'
        ? 'off'
        : 'partner';
  const warpThemeChannel: WarpThemeChannel = (() => {
    const raw = init?.settings?.warp_theme_channel as
      | WarpThemeChannel
      | undefined;
    return raw && WARP_CHANNELS.includes(raw) ? raw : 'teal';
  })();
  const warpCompact = init?.settings?.warp_compact === 'true';
  const largeDotMountEnabled =
    dotLargeEnabled && !(warpEnabled && warpMode === 'substitute');

  // Stage 200: GPP Laughter Module gate. Forward-only toggle — flipping
  // off stops new writes; existing engrams remain. The subscriber
  // mounts regardless; its useEffect short-circuits when the gate is
  // false (zero work, byte-equivalent to the Stage 97 baseline).
  const laughterEnabled = init?.settings?.laughter_enabled === 'true';

  // Stage 201: GPP Laughter horizon sub-stage gates. All three default
  // off and are independent; each gates exactly one sub-stage. With all
  // three off, behaviour is byte-equivalent to the Stage 200 baseline.
  const laughterWarpMarkerEnabled =
    init?.settings?.laughter_warp_marker_enabled === 'true';
  const laughterCorrelationsEnabled =
    init?.settings?.laughter_correlations_enabled === 'true';
  const laughterWitnessEnabled =
    init?.settings?.laughter_witness_enabled === 'true';
  // Stage 202-J: deep shared-interpretive-ground joint detector.
  const laughterSharedGroundEnabled =
    init?.settings?.laughter_shared_ground_enabled === 'true';

  // Auto-load Kokoro model at app mount when the provider is set to kokoro.
  // Idempotent (ensureReady short-circuits when already ready); with the
  // transformers-cache populated from a prior session this resolves in
  // roughly a second. First-ever load triggers the ~88 MB download.
  useEffect(() => {
    if (ttsProvider !== 'kokoro') return;
    if (kokoroClient.getState() === 'ready') return;
    void kokoroClient.ensureReady().catch((err) => {
      console.warn('[kokoro] auto-load failed:', err);
    });
  }, [ttsProvider]);

  // Auto-load the voice bundle at app mount when blend mode is active, so
  // the very first chat reply after a hard reload can synthesise without a
  // manual Settings → Download voices click.
  useEffect(() => {
    if (ttsProvider !== 'kokoro') return;
    if (kokoroBlendMode !== 'blend') return;
    if (voiceBundle.getState() === 'ready') return;
    void voiceBundle.ensureReady().catch((err) => {
      console.warn('[kokoro] voice-bundle auto-load failed:', err);
    });
  }, [ttsProvider, kokoroBlendMode]);

  // Pin lookup for the active agent.
  const [pins, setPins] = useState<Record<string, KokoroPin>>({});
  useEffect(() => {
    if (ttsProvider !== 'kokoro') return;
    let cancelled = false;
    void listPins()
      .then((p) => {
        if (!cancelled) setPins(p);
      })
      .catch(() => {
        /* ignore — settings tab surfaces errors */
      });
    return () => {
      cancelled = true;
    };
  }, [ttsProvider, init?.settings?.kokoro_world_seed, activeAgentName]);
  const activePin = activeAgentName ? pins[activeAgentName] : undefined;

  // Parse spec_json into a VoiceSpec if present (blend-mode pin).
  const activePinnedSpec: VoiceSpec | null = (() => {
    const raw = activePin?.spec_json;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<VoiceSpec>;
      if (
        typeof parsed.voiceA === 'number' &&
        typeof parsed.voiceB === 'number' &&
        typeof parsed.t === 'number' &&
        typeof parsed.pitch === 'number' &&
        typeof parsed.energy === 'number'
      ) {
        return parsed as VoiceSpec;
      }
    } catch {
      /* ignore malformed spec_json */
    }
    return null;
  })();

  // Stage 26: ZeroTTS instant-speech cache. Reads settings and the active
  // persona name; runs the install/lookup/playback lifecycle. Only active
  // when the user enables it AND the provider is Kokoro (ZeroTTS uses the
  // same WASM Kokoro session to render its cached atoms).
  const zeroTTSEnabled = init?.settings?.zerotts_enabled !== 'false';
  const zeroTTSFireAck = init?.settings?.zerotts_fire_ack !== 'false';
  const zeroTTSFireToolInvoke =
    init?.settings?.zerotts_fire_tool_invoke !== 'false';
  const zeroTTSFireProgress =
    init?.settings?.zerotts_fire_progress === 'true';
  const zeroTTSFireError = init?.settings?.zerotts_fire_error !== 'false';
  // Stage 40: cache depth. Live chat path needs the SAME depth the user
  // has installed via the ZeroVoice tab, otherwise the player's
  // fingerprint won't match the manifest and atoms cache-miss.
  const zeroTTSDepth = asZeroTTSDepth(init?.settings?.zerotts_depth);
  const zeroTTS = useZeroTTS({
    personaName: activeAgentName,
    worldSeedHex: kokoroWorldSeed,
    defaultGender: kokoroDefaultGender,
    enabled: ttsProvider === 'kokoro' && zeroTTSEnabled,
    // Stage 26.6: when the user has pinned a blended VoiceSpec and the
    // global kokoro_blend_mode is 'blend', render the atom cache with that
    // exact spec so the filler voice matches the live reply voice.
    blendMode: kokoroBlendMode,
    pinnedSpec: activePinnedSpec,
    depth: zeroTTSDepth,
  });

  const chat = useChat({
    onStateChange: (s) => orchestrator.setState(s),
    speakResponses,
    ttsProvider,
    kokoroAgentName: activeAgentName,
    kokoroAgentGender: activePin?.gender ?? 'default',
    kokoroDefaultGender,
    kokoroPinnedVoiceId: activePin?.voice_id ?? null,
    kokoroWorldSeed,
    kokoroFallbackToGemini,
    kokoroBlendMode,
    kokoroPinnedSpec: activePinnedSpec,
    speechSummaryEnabled,
    speechFallback,
    zeroTTS: {
      speak: zeroTTS.speak,
      stopActive: zeroTTS.stopActive,
      enabledContexts: {
        ack: zeroTTSEnabled && zeroTTSFireAck,
        tool_invoke: zeroTTSEnabled && zeroTTSFireToolInvoke,
        progress: zeroTTSEnabled && zeroTTSFireProgress,
        error: zeroTTSEnabled && zeroTTSFireError,
      },
    },
  });

  // Live (audio-to-audio) session state is shared between the header
  // toggle button and the floating transcript panel, so the hook is
  // owned here rather than inside LiveVoiceButton.
  const live = useLiveVoice();

  // Stage 14: HUD derivations. All three pill fields and the merged
  // state indicator resolve from data already flowing through the app.
  const [voicePresets, setVoicePresets] = useState<VoicePresetLite[]>([]);
  useEffect(() => {
    let cancelled = false;
    void listVoicePresets()
      .then((list) => {
        if (!cancelled) setVoicePresets(list);
      })
      .catch(() => {
        /* best-effort: HUD falls back to base voice when fetch fails */
      });
    return () => {
      cancelled = true;
    };
  }, [init?.settings?.default_voice_preset_id]);

  const moodDynamic = init?.settings?.mood_dynamic === 'true';
  const latestMoodMsg = [...chat.messages]
    .reverse()
    .find((m) => m.role === 'assistant' && m.mood);
  const latestMood = latestMoodMsg?.mood ?? null;

  // Stage 200: Laughter subscriber inputs. The user message + the
  // persona response of the last completed turn are read from
  // chat.messages so the Shape-Q engram carries self-interpretable
  // turn context (canonical TINS §Data Models — the engram is a record
  // of the reframe, not a bare pointer; the user message + persona
  // response are the cognitive backdrop the laugh happened against).
  const lastAssistantMsg = [...chat.messages]
    .reverse()
    .find((m) => m.role === 'assistant' && !!m.text);
  const lastUserMsg = (() => {
    if (!lastAssistantMsg) return null;
    const idx = chat.messages.findIndex((m) => m.id === lastAssistantMsg.id);
    for (let i = idx - 1; i >= 0; i--) {
      const m = chat.messages[i];
      if (m.role === 'user' && m.text) return m;
    }
    return null;
  })();
  // Stage 201-P: the mood snapshot from the same last assistant message
  // feeds the Laughter correlation payload (mood region at fire time).
  const lastAssistantMood = lastAssistantMsg?.mood ?? null;
  const hudMood: string | null = (() => {
    if (!moodDynamic) return init?.activePersona?.mood ?? 'baseline';
    if (latestMood) return latestMood.label.toLowerCase();
    return init?.activePersona?.mood ?? 'baseline';
  })();
  const hudMoodShifted = latestMood?.signalSource === 'model';
  // Stage 45: latest mood label drives the large-dot background.
  // Mirrors the hudMood derivation; do not collapse them — this
  // one is intentionally case-insensitive and ignores moodDynamic
  // because the mood-tinted background is a direct expression of
  // the assistant's stated mood, not the persona baseline.
  const latestMoodLabel: string | null =
    latestMood?.label?.toLowerCase() ?? null;
  const hudMoodTooltip = (() => {
    const parts: string[] = [];
    if (init?.activePersona?.mood)
      parts.push(`baseline: ${init.activePersona.mood}`);
    if (latestMood?.alpha != null)
      parts.push(`α=${latestMood.alpha.toFixed(2)}`);
    if (latestMood) parts.push(`source: ${latestMood.signalSource}`);
    return parts.length > 0 ? parts.join(' · ') : undefined;
  })();

  const hudVoice = (() => {
    if (ttsProvider === 'kokoro') {
      const blend = kokoroBlendMode === 'blend';
      if (activePin) {
        if (blend && activePin.spec_json) return 'Kokoro · blend';
        return `Kokoro · ${activePin.voice_id}`;
      }
      return blend ? 'Kokoro · blend · auto' : 'Kokoro · auto';
    }
    const presetId = init?.settings?.default_voice_preset_id;
    if (presetId) {
      const hit = voicePresets.find((p) => p.id === presetId);
      if (hit) return `Gemini · ${hit.name}`;
    }
    const base =
      init?.settings?.default_tts_voice ||
      init?.activePersona?.voice ||
      'Kore';
    return `Gemini · ${base}`;
  })();
  const hudVoiceTooltip = (() => {
    const parts: string[] = [`provider: ${ttsProvider}`];
    if (ttsProvider === 'gemini') {
      const presetId = init?.settings?.default_voice_preset_id;
      if (presetId) parts.push(`preset: ${presetId}`);
      parts.push(
        `base: ${
          init?.settings?.default_tts_voice ||
          init?.activePersona?.voice ||
          'Kore'
        }`,
      );
    } else {
      parts.push(`blend: ${kokoroBlendMode}`);
      if (activePin) parts.push(`pin: ${activePin.voice_id}`);
    }
    return parts.join(' · ');
  })();

  const hudState: HudState = (() => {
    if (live.state === 'connecting') return 'connecting';
    if (live.state === 'listening') return 'listening';
    if (live.state === 'speaking') return 'speaking';
    if (live.state === 'error') return 'error';
    if (live.state === 'ready' && orchestrator.state === 'idle')
      return 'live-ready';
    return orchestrator.state;
  })();

  const hudDisplayName =
    init?.activePersona?.full_name || init?.activePersona?.name || '';

  const onNewChat = (): void => {
    // Switch to chat view first, so invoking New chat from a gallery page
    // lands the user where they expect (Stage 9b).
    setView('chat');
    chat.reset();
    // Focus the InputBar textarea on next tick so the user can start typing.
    setTimeout(() => {
      const ta = document.querySelector<HTMLTextAreaElement>(
        '.input-bar textarea',
      );
      ta?.focus();
    }, 0);
  };

  // Global keyboard shortcuts: Esc closes Settings/Skills when open; Cmd/Ctrl+,
  // toggles Settings; Cmd/Ctrl+K toggles Skills; Cmd/Ctrl+Shift+N starts a new chat.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && settingsOpen) {
        setSettingsOpen(false);
        void refresh();
        return;
      }
      if (e.key === 'Escape' && skillsOpen) {
        setSkillsOpen(false);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        setSettingsOpen((open) => !open);
        return;
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === 'k'
      ) {
        e.preventDefault();
        setSkillsOpen((open) => !open);
        return;
      }
      // Stage 9b/16: Cmd/Ctrl + 1..6 jumps between primary views.
      if (
        !settingsOpen &&
        !skillsOpen &&
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey
      ) {
        const viewMap: Record<string, PrimaryView> = {
          '1': 'chat',
          '2': 'projects',
          '3': 'images',
          '4': 'music',
          '5': 'speech',
          '6': 'video',
          '7': 'documents',
          '8': 'yesterday',
          // Stage 48: jump to the Connections page.
          '9': 'connections',
        };
        const target = viewMap[e.key];
        if (target) {
          e.preventDefault();
          setView(target);
          return;
        }
      }
      if (
        !settingsOpen &&
        !skillsOpen &&
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'n'
      ) {
        e.preventDefault();
        onNewChat();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen, skillsOpen, refresh]);

  if (loading && !init) {
    return <div className="loading-screen">Initializing…</div>;
  }
  if (error && !init) {
    return (
      <div className="loading-screen">
        <div className="status-missing">Init error: {error}</div>
        <button onClick={() => void refresh()}>Retry</button>
      </div>
    );
  }
  if (!init) {
    return <div className="loading-screen">No init payload.</div>;
  }

  const apiKeyConfigured = init.apiKeyConfigured;

  return (
    <div className="app-shell">
      <header className="app-header">
        <span className="brand">SYNTH-WEB</span>
        <div className="app-header-persona">
          <PersonaHeader
            displayName={hudDisplayName}
            mood={hudMood}
            moodShifted={hudMoodShifted}
            moodTooltip={hudMoodTooltip}
            voice={hudVoice}
            voiceTooltip={hudVoiceTooltip}
            state={hudState}
          />
        </div>
        <div className="spacer" />
        {liveVoiceEnabled ? (
          <LiveVoiceButton
            live={live}
            conversationId={chat.conversationId ?? null}
          />
        ) : null}
        {/* Stage 45: when the large sidebar widget is enabled AND
            the user is on the chat view, hide the header DOT so the
            two widgets do not double-render the same audio bus. */}
        {dotLargeEnabled && view === 'chat' ? null : (
          <DotWidget
            enabled={dotEnabled}
            activeProfileId={dotActiveProfileId}
            color={dotColor}
            wireframe={dotWireframe}
            sensitivity={dotSensitivity}
          />
        )}
        <button
          type="button"
          className={
            'mute-toggle' +
            (audio.muted ? ' mute-toggle-muted' : '') +
            (audio.playing ? ' mute-toggle-playing' : '')
          }
          onClick={() => audio.setMuted(!audio.muted)}
          aria-label={audio.muted ? 'Unmute voice output' : 'Mute voice output'}
          title={audio.muted ? 'Voice output muted' : 'Voice output on'}
        >
          <SpeakerIcon muted={audio.muted} />
        </button>
        <button
          type="button"
          className={view === 'chat' ? 'btn-primary' : ''}
          onClick={() => setView('chat')}
          aria-pressed={view === 'chat'}
        >
          Chat
        </button>
        <button
          type="button"
          className={view === 'projects' ? 'btn-primary' : ''}
          onClick={() => setView('projects')}
          aria-pressed={view === 'projects'}
        >
          Projects
        </button>
        <button
          type="button"
          className={view === 'images' ? 'btn-primary' : ''}
          onClick={() => setView('images')}
          aria-pressed={view === 'images'}
          title="Images (Cmd/Ctrl+3)"
        >
          🖼️ Images
        </button>
        <button
          type="button"
          className={view === 'music' ? 'btn-primary' : ''}
          onClick={() => setView('music')}
          aria-pressed={view === 'music'}
          title="Music (Cmd/Ctrl+4)"
        >
          🎵 Music
        </button>
        <button
          type="button"
          className={view === 'speech' ? 'btn-primary' : ''}
          onClick={() => setView('speech')}
          aria-pressed={view === 'speech'}
          title="Speech (Cmd/Ctrl+5)"
        >
          🔊 Speech
        </button>
        {/* Stage 27: Video tab always visible so users can view and download
            existing clips even when new generation is disabled. */}
        <button
          type="button"
          className={view === 'video' ? 'btn-primary' : ''}
          onClick={() => setView('video')}
          aria-pressed={view === 'video'}
          title={
            anyVideoGenEnabled
              ? 'Video (Cmd/Ctrl+6)'
              : 'Video gallery (generation disabled in Settings → Permissions)'
          }
        >
          🎬 Video
        </button>
        {/* Stage 28: Documents tab — artifacts saved by the
            write_markdown_file tool (TINS outputs, plans, reports). */}
        <button
          type="button"
          className={view === 'documents' ? 'btn-primary' : ''}
          onClick={() => setView('documents')}
          aria-pressed={view === 'documents'}
          title="Documents (Cmd/Ctrl+7)"
        >
          📄 Documents
        </button>
        <button
          type="button"
          className={view === 'yesterday' ? 'btn-primary' : ''}
          onClick={() => setView('yesterday')}
          aria-pressed={view === 'yesterday'}
          title="Yesterday (Cmd/Ctrl+8)"
        >
          📅 Yesterday
        </button>
        {/* Stage 33: Bulletin Canvas — endless panel canvas backed by
            scheduled agentic tasks. */}
        <button
          type="button"
          className={view === 'bulletin' ? 'btn-primary' : ''}
          onClick={() => setView('bulletin')}
          aria-pressed={view === 'bulletin'}
          title="Bulletin Canvas"
        >
          📌 Bulletin
        </button>
        {/* Stage 44: LIBRARIAN-Wiki — knowledge wiki built from ingested
            artifacts. Button is always visible so the operator can reach
            Settings → Wiki even when the master flag is off; the route
            itself surfaces a friendly "wiki disabled" banner in that case. */}
        <button
          type="button"
          className={view === 'wiki' ? 'btn-primary' : ''}
          onClick={() => setView('wiki')}
          aria-pressed={view === 'wiki'}
          title="LIBRARIAN-Wiki"
        >
          📚 Wiki
        </button>
        {/* Stage 48: Remote Gallery Connections — full-page section for
            managing API tokens that grant remote apps read-only access to
            the gallery. Always visible so the operator can reach the
            master switch even when the system is disabled. */}
        <button
          type="button"
          className={view === 'connections' ? 'btn-primary' : ''}
          onClick={() => setView('connections')}
          aria-pressed={view === 'connections'}
          title="Connections (Cmd/Ctrl+9)"
        >
          🔗 Connections
        </button>
        <button
          type="button"
          onClick={() => setSkillsOpen(true)}
          aria-label="Open Skills"
          title="Skills (Cmd/Ctrl+K)"
        >
          🧠 Skills
        </button>
        <span className="muted small">{userEmail}</span>
        <button
          type="button"
          onClick={onNewChat}
          disabled={chat.busy}
          aria-label="Start a new chat"
          title="Start a new chat (Cmd/Ctrl+Shift+N)"
        >
          New chat
        </button>
        <button onClick={() => setSettingsOpen(true)}>Settings</button>
        <button onClick={signOut}>Sign out</button>
      </header>

      {!apiKeyConfigured ? (
        <div className="banner banner-warning">
          Add your Gemini API key in Settings to begin.
        </div>
      ) : null}

      {view === 'chat' ? (
        <>
          <div className="chat-with-sidebar">
            <ConversationSidebar
              activeConversationId={chat.conversationId}
              reloadKey={chat.messages.length}
              onPick={(conversationId) => {
                if (conversationId === chat.conversationId) return;
                void chat
                  .hydrateConversation(conversationId)
                  .catch((e) => {
                    // eslint-disable-next-line no-console
                    console.warn('[sidebar] hydrate failed:', e);
                  })
                  .finally(() => {
                    setTimeout(() => {
                      const ta =
                        document.querySelector<HTMLTextAreaElement>(
                          '.input-bar textarea',
                        );
                      ta?.focus();
                    }, 0);
                  });
              }}
            />
            <main className="chat-main">
              <ChatTranscript
                messages={chat.messages}
                onFork={
                  chat.conversationId
                    ? (turnIndex) => {
                        const sourceId = chat.conversationId;
                        if (!sourceId) return;
                        void (async () => {
                          try {
                            const { apiJson } = await import('../api/client');
                            const res = await apiJson<{ conversation_id: string }>(
                              'POST',
                              `/api/conversations/${encodeURIComponent(sourceId)}/fork`,
                              { upToTurnIndex: turnIndex },
                            );
                            if (res.conversation_id) {
                              await chat.hydrateConversation(res.conversation_id);
                            }
                          } catch (e) {
                            // eslint-disable-next-line no-console
                            console.warn('[fork] failed:', e);
                          }
                        })();
                      }
                    : undefined
                }
              />
            </main>
            {/* Stage 45: large DOT, right edge of chat layout.
                Sibling of ConversationSidebar / chat-main; the
                .chat-with-sidebar flex row grows a third child
                when this is rendered. Returns null internally
                when dotLargeEnabled is false.
                Stage 97: when WARP is in substitute mode, the
                LargeDotWidget is forced off via `largeDotMountEnabled`
                (the audio-bus cleanup inside LargeDotWidget depends
                on the existing `if (!enabled) return null` branch,
                so we route the override through the prop, not a JSX
                unmount). */}
            <LargeDotWidget
              enabled={largeDotMountEnabled}
              activeProfileId={dotActiveProfileId}
              color={dotColor}
              wireframe={dotWireframe}
              sensitivity={dotSensitivity}
              moodLabel={latestMoodLabel}
              moodBackgroundEnabled={dotLargeMoodBg}
              idleMode={dotLargeIdleMode}
              workingMode={dotLargeWorkingMode}
              orchestratorState={orchestrator.state}
            />
            {/* Stage 97: WARP / ZERO Confusion Monitor. Mounts when
                warp_enabled='true' AND warp_mode is 'partner' or
                'substitute'. Partner mode renders BELOW LargeDotWidget;
                substitute mode renders alone (LargeDotWidget is forced
                off above). Returns null internally when the gate is
                false. */}
            <WarpWidget
              enabled={warpEnabled && warpMode !== 'off'}
              themeChannel={warpThemeChannel}
              compact={warpCompact}
              partneredWithDot={warpMode === 'partner' && dotLargeEnabled}
            />
            {/* Stage 200: GPP Laughter Module — first GPP pillar.
                Logic-only, render-null. Subscribes to useGadflyTurn and
                posts a Shape-Q engram per finally-ACCEPTed bite at
                turn-end. When laughter_enabled === 'false', the hook's
                useEffect short-circuits at the gate (zero work). Sibling
                to WarpWidget; consumes the same Stage-97 turn-end seam. */}
            <LaughterSubscriber
              enabled={laughterEnabled}
              conversationId={chat.conversationId ?? null}
              userMessage={lastUserMsg?.text ?? null}
              personaResponse={lastAssistantMsg?.text ?? null}
              warpMarkerEnabled={laughterWarpMarkerEnabled}
              correlationsEnabled={laughterCorrelationsEnabled}
              lastMood={lastAssistantMood}
            />
            {/* Stage 201-S: witness + joint reframe subscriber. Watches the
                settled turn for a witness pattern-flip (lexical heuristic)
                and, on same-turn self-laugh co-occurrence, a joint reframe.
                Gated by laughter_enabled AND laughter_witness_enabled —
                witness detection is meaningless without Laughter on. */}
            <WitnessReframeSubscriber
              enabled={laughterEnabled && laughterWitnessEnabled}
              conversationId={chat.conversationId ?? null}
              latestUserMsg={lastUserMsg ?? null}
              sharedGroundEnabled={laughterSharedGroundEnabled}
            />
          </div>

          <InputBar
            onSend={(t, attachment) => void chat.sendMessage(t, attachment)}
            disabled={!apiKeyConfigured || chat.busy}
            apiKeyConfigured={apiKeyConfigured}
            enabledSkills={init?.activePersona?.enabled_skills ?? []}
          />
        </>
      ) : view === 'projects' ? (
        <main className="chat-main">
          <ProjectsView
            onOpenChat={(conversationId) => {
              // Stage 11b: switch the view first so the transition is
              // visible, then hydrate the stored conversation into useChat.
              // setView before await so the UI doesn't appear frozen while
              // the GET is in flight.
              setView('chat');
              void chat
                .hydrateConversation(conversationId)
                .catch((err) => {
                  // eslint-disable-next-line no-console
                  console.warn('[open-chat] hydrate failed:', err);
                })
                .finally(() => {
                  setTimeout(() => {
                    const ta =
                      document.querySelector<HTMLTextAreaElement>(
                        '.input-bar textarea',
                      );
                    ta?.focus();
                  }, 0);
                });
            }}
          />
        </main>
      ) : view === 'images' ? (
        <main className="gallery-main">
          <GalleryView title="Generated images">
            <ImageGallery />
          </GalleryView>
        </main>
      ) : view === 'music' ? (
        <main className="gallery-main">
          <GalleryView title="Music tracks">
            <MusicGallery />
          </GalleryView>
        </main>
      ) : view === 'video' ? (
        <main className="gallery-main">
          <GalleryView title="Video gallery">
            <VideoGallery videoGenEnabled={anyVideoGenEnabled} />
          </GalleryView>
        </main>
      ) : view === 'documents' ? (
        <main className="gallery-main">
          <GalleryView title="Documents">
            <DocumentGallery />
          </GalleryView>
        </main>
      ) : view === 'yesterday' ? (
        <main className="gallery-main">
          <GalleryView title="Yesterday — scheduled prompts">
            <YesterdayTab />
          </GalleryView>
        </main>
      ) : view === 'bulletin' ? (
        // Stage 33: bulletin canvas owns its own layout chrome (no
        // GalleryView wrapper) so the canvas can claim full height for
        // pan/zoom.
        <main className="gallery-main">
          <BulletinRoute />
        </main>
      ) : view === 'wiki' ? (
        // Stage 44: LIBRARIAN-Wiki — knowledge wiki built from ingested
        // artifacts. The route itself handles sub-paths (#/wiki/p/:pid/...)
        // and surfaces a "wiki disabled" banner when the master flag is off.
        <main className="gallery-main">
          <WikiRoute />
        </main>
      ) : view === 'connections' ? (
        // Stage 48: Remote Gallery Connections — full-page section for
        // managing API tokens granted to remote apps. Renders its own
        // master switch + list / wizard / audit drawer; the page itself
        // shows a "connections disabled" explainer when the master flag
        // is off.
        <main className="gallery-main">
          <ConnectionsView />
        </main>
      ) : (
        <main className="gallery-main">
          <GalleryView title="Speech clips">
            <SpeechGallery />
          </GalleryView>
        </main>
      )}

      {/* Hidden audio playback host. useAudioPlayer owns a singleton
          HTMLAudioElement internally; this marker aids a11y tools. */}
      <div className="audio-host" aria-hidden="true" />

      {settingsOpen ? (
        <SettingsModal
          onClose={() => {
            setSettingsOpen(false);
            void refresh();
          }}
          onChanged={() => void refresh()}
          currentConversationId={chat.conversationId ?? null}
        />
      ) : null}

      {skillsOpen ? (
        <SkillsModal onClose={() => setSkillsOpen(false)} />
      ) : null}

      {liveVoiceEnabled ? (
        <LiveTranscriptPanel
          state={live.state}
          transcript={live.transcript}
          outputTranscript={live.outputTranscript}
          error={live.error}
        />
      ) : null}
    </div>
  );
}
